import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button, Input, Select, Form, message } from 'antd';
import styles from './s.module.less';
import { IClosedFunnelDetail } from 'types/operation';
import { updateClosedFunnelBasicInfo } from 'api/operation/closedFunnel';

interface InfoModalProps {
    info?: IClosedFunnelDetail;
    conditionOptions?: { label: string; value: string }[];
    statesOptions?: { label: string; value: string }[];
    typeOptions?: { label: string; value: string }[];
    open: boolean;
    onSuccess: () => void;
    onCancel: () => void;
}

const InfoModal: React.FC<InfoModalProps> = ({
    info,
    conditionOptions,
    statesOptions,
    typeOptions,
    open,
    onSuccess,
    onCancel,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (info && info.basicInfo) {
            form.setFieldsValue({
                name: info.basicInfo.name,
                states: info.basicInfo.states.split(','),
                type: info.basicInfo.type,
                typeValues: info.basicInfo.typeValues.split(','),
                link: info.basicInfo.link,
            });
        }
    }, [form, info]);

    const handleSubmit = useCallback(() => {
        if (info?.basicInfo?.id) {
            form.validateFields().then(async (values) => {
                setLoading(true);
                try {
                    const res = await updateClosedFunnelBasicInfo(info.basicInfo.id, {
                        ...values,
                        states: values.states.join(','),
                        typeValues: values.typeValues.join(','),
                    });
                    if (res && !res.error) {
                        onSuccess?.();
                    } else {
                        message.error(res?.message);
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    setLoading(false);
                }
            });
        }
    }, [form, info, onSuccess]);

    return (
        <Modal
            open={open}
            footer={null}
            closable={false}
            className={styles.modal}
            width={680}
            centered
            destroyOnClose
        >
            <div className={styles.header}>
                <span className={styles.title}>Funnel information</span>
                <div className={styles.headerBtns}>
                    <Button className={styles.cancelBtn} onClick={onCancel}>Cancel</Button>
                    <Button className={styles.saveBtn} type="primary" onClick={handleSubmit} loading={loading}>Save changes</Button>
                </div>
            </div>
            <div className={styles.requiredTip}><span className={styles.red}>*</span> Indicates required fields</div>
            <Form
                form={form}
                layout="vertical"
            >
                <Form.Item
                    label={<span className={styles.label}>Funnel name</span>}
                    name="name"
                    rules={[{ required: true, message: 'Please input funnel name' }, { max: 70, message: 'Max 70 characters' }]}
                >
                    <Input className={styles.input} maxLength={70} showCount />
                </Form.Item>
                <Form.Item
                    label={<span className={styles.label}>Applicable states</span>}
                    name="states"
                    rules={[{ required: true, message: 'Please select states' }]}
                >
                    <Select
                        mode="multiple"
                        allowClear
                        maxTagCount="responsive"
                        options={statesOptions}
                        className={styles.select}
                        placeholder="Select states"
                    />
                </Form.Item>
                <Form.Item
                    label={<span className={styles.label}>Type</span>}
                    name="type"
                    rules={[{ required: true, message: 'Please select type' }]}
                >
                    <Select
                        options={typeOptions}
                        className={styles.select}
                        placeholder="Select type"
                    />
                </Form.Item>
                <Form.Item
                    label={<span className={styles.label}>Condition</span>}
                    name="typeValues"
                    rules={[{ required: true, message: 'Please select condition' }]}
                >
                    <Select
                        mode="multiple"
                        options={conditionOptions}
                        className={styles.select}
                        placeholder="Select condition"
                    />
                </Form.Item>
                <Form.Item
                    label={<span className={styles.label}>Link</span>}
                    name="link"
                    rules={[{ required: true, message: 'Link is required' }]}
                >
                    <Input className={styles.input} readOnly disabled />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default InfoModal;
